import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ArrowUpDown, ShieldCheck, FileWarning, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { regenerateDocumentPdf } from "@/lib/regeneratePdf";
import { cn } from "@/lib/utils";
import {
  RULE_GROUP_ORDER,
  RULE_GROUP_LABELS,
  STATUS_CLASS,
  STATUS_LABEL,
  type ItemStatus,
} from "@/lib/governanceHealth";

const PERIOD_YEAR = 2026;
type SortKey = "score" | "overdue" | "days";

interface RuleRow {
  rule_key: string;
  title: string;
  rule_group: string;
  cadence: string | null;
}
interface UnaRow {
  id: string;
  name: string;
  treasury_secretary_id: string | null;
  ts_name: string | null;
  score: number | null;
  overdue_count: number;
  foundation_satisfied: number;
  foundation_total: number;
  days_since: number | null;
  journey_status: "completed" | "in_progress" | "not_started";
}

export default function AdminGovernanceHealth() {
  const [sortKey, setSortKey] = useState<SortKey>("score");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [selectedUna, setSelectedUna] = useState<UnaRow | null>(null);
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillProgress, setBackfillProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 });
  const [backfillFailures, setBackfillFailures] = useState<{ name: string; error: string }[]>([]);

  const missingPdfs = useQuery({
    queryKey: ["admin-gh-missing-pdfs"],
    queryFn: async () => {
      const { data, error, count } = await supabase
        .from("generated_documents")
        .select("id, document_name", { count: "exact" })
        .is("file_path", null)
        .is("file_url", null)
        .in("status", ["final", "finalized"]);
      if (error) throw error;
      return { rows: (data ?? []) as { id: string; document_name: string }[], count: count ?? 0 };
    },
  });

  const runBackfill = async () => {
    const rows = missingPdfs.data?.rows ?? [];
    if (rows.length === 0) return;
    setBackfillRunning(true);
    setBackfillFailures([]);
    setBackfillProgress({ done: 0, total: rows.length });
    const failures: { name: string; error: string }[] = [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      try {
        const res = await regenerateDocumentPdf(row.id);
        if (res.error) failures.push({ name: row.document_name, error: res.error });
      } catch (e: any) {
        failures.push({ name: row.document_name, error: e?.message || "Unknown error" });
      }
      setBackfillProgress({ done: i + 1, total: rows.length });
      // small delay between renders to avoid hammering PDFShift
      await new Promise((r) => setTimeout(r, 400));
    }
    setBackfillFailures(failures);
    setBackfillRunning(false);
    const ok = rows.length - failures.length;
    if (failures.length === 0) {
      toast.success(`Regenerated ${ok} of ${rows.length} documents`);
    } else {
      toast.warning(`Regenerated ${ok} of ${rows.length}; ${failures.length} failed`);
    }
    missingPdfs.refetch();
  };

  const { data: catalog = [] } = useQuery({
    queryKey: ["admin-gh-catalog"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("compliance_rule_catalog")
        .select("rule_key, title, rule_group, cadence");
      if (error) throw error;
      return (data ?? []) as RuleRow[];
    },
  });

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["admin-gh-overview", PERIOD_YEAR],
    queryFn: async () => {
      const [unasRes, scoresRes, itemsRes] = await Promise.all([
        supabase.from("unas").select("id, name, treasury_secretary_id"),
        supabase
          .from("una_governance_scores")
          .select("una_id, score")
          .eq("period_year", PERIOD_YEAR),
        supabase
          .from("una_compliance_items")
          .select("una_id, rule_key, rule_group, status"),
      ]);
      if (unasRes.error) throw unasRes.error;
      if (scoresRes.error) throw scoresRes.error;
      if (itemsRes.error) throw itemsRes.error;

      const unas = unasRes.data ?? [];
      const secretaryIds = Array.from(
        new Set(unas.map((u) => u.treasury_secretary_id).filter(Boolean) as string[])
      );

      const [profilesRes, journeyRes, resRes, jnlRes, mtgRes] = await Promise.all([
        secretaryIds.length
          ? supabase.from("profiles").select("id, full_name").in("id", secretaryIds)
          : Promise.resolve({ data: [], error: null } as any),
        secretaryIds.length
          ? supabase
              .from("user_journey_state")
              .select("user_id, journey_completed, current_phase")
              .in("user_id", secretaryIds)
          : Promise.resolve({ data: [], error: null } as any),
        supabase.from("una_resolutions").select("una_id, resolution_date"),
        supabase.from("una_journal_records").select("una_id, record_date"),
        supabase
          .from("meeting_minutes")
          .select("una_id, meeting_date, certified_at")
          .not("certified_at", "is", null),
      ]);
      if (profilesRes.error) throw profilesRes.error;
      if (journeyRes.error) throw journeyRes.error;
      if (resRes.error) throw resRes.error;
      if (jnlRes.error) throw jnlRes.error;
      if (mtgRes.error) throw mtgRes.error;

      const scoreBy = new Map<string, number>();
      (scoresRes.data ?? []).forEach((r: any) => scoreBy.set(r.una_id, r.score));

      const nameBy = new Map<string, string>();
      (profilesRes.data ?? []).forEach((p: any) => nameBy.set(p.id, p.full_name));

      const journeyBy = new Map<string, any>();
      (journeyRes.data ?? []).forEach((j: any) => journeyBy.set(j.user_id, j));

      const latestBy = new Map<string, number>();
      const bump = (unaId: string, dateStr: string | null) => {
        if (!dateStr) return;
        const t = new Date(dateStr).getTime();
        if (!isFinite(t)) return;
        const prev = latestBy.get(unaId);
        if (prev === undefined || t > prev) latestBy.set(unaId, t);
      };
      (resRes.data ?? []).forEach((r: any) => bump(r.una_id, r.resolution_date));
      (jnlRes.data ?? []).forEach((r: any) => bump(r.una_id, r.record_date));
      (mtgRes.data ?? []).forEach((r: any) => bump(r.una_id, r.meeting_date));

      const overdueBy = new Map<string, number>();
      const foundationSatBy = new Map<string, number>();
      const foundationTotalBy = new Map<string, number>();
      (itemsRes.data ?? []).forEach((it: any) => {
        if (it.status === "overdue") {
          overdueBy.set(it.una_id, (overdueBy.get(it.una_id) ?? 0) + 1);
        }
        if (it.rule_group === "foundation") {
          foundationTotalBy.set(it.una_id, (foundationTotalBy.get(it.una_id) ?? 0) + 1);
          if (it.status === "satisfied") {
            foundationSatBy.set(it.una_id, (foundationSatBy.get(it.una_id) ?? 0) + 1);
          }
        }
      });

      const now = Date.now();
      const built: UnaRow[] = unas.map((u: any) => {
        const j = u.treasury_secretary_id ? journeyBy.get(u.treasury_secretary_id) : null;
        const journey_status: UnaRow["journey_status"] = !u.treasury_secretary_id
          ? "not_started"
          : j?.journey_completed
          ? "completed"
          : j
          ? "in_progress"
          : "not_started";
        const latest = latestBy.get(u.id);
        return {
          id: u.id,
          name: u.name,
          treasury_secretary_id: u.treasury_secretary_id,
          ts_name: u.treasury_secretary_id
            ? nameBy.get(u.treasury_secretary_id) ?? null
            : null,
          score: scoreBy.get(u.id) ?? null,
          overdue_count: overdueBy.get(u.id) ?? 0,
          foundation_satisfied: foundationSatBy.get(u.id) ?? 0,
          foundation_total: foundationTotalBy.get(u.id) ?? 0,
          days_since: latest ? Math.floor((now - latest) / 86400000) : null,
          journey_status,
        };
      });
      return built;
    },
  });

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      let av: number, bv: number;
      switch (sortKey) {
        case "overdue":
          av = a.overdue_count;
          bv = b.overdue_count;
          break;
        case "days":
          av = a.days_since ?? Number.POSITIVE_INFINITY;
          bv = b.days_since ?? Number.POSITIVE_INFINITY;
          break;
        default:
          av = a.score ?? Number.POSITIVE_INFINITY;
          bv = b.score ?? Number.POSITIVE_INFINITY;
      }
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  const summary = useMemo(() => {
    const scored = rows.filter((r) => r.score !== null);
    const avg =
      scored.length === 0
        ? 0
        : Math.round(
            scored.reduce((s, r) => s + (r.score ?? 0), 0) / scored.length
          );
    return {
      avg,
      below30: rows.filter((r) => (r.score ?? 0) < 30).length,
      overdue: rows.reduce((s, r) => s + r.overdue_count, 0),
      stale90: rows.filter(
        (r) => r.days_since === null || r.days_since >= 90
      ).length,
      totalUnas: rows.length,
    };
  }, [rows]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "score" ? "asc" : "desc");
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold">Governance Health Overview</h1>
          <p className="text-sm text-muted-foreground">
            Operator-only view. Read only.
          </p>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Average score" value={String(summary.avg)} sub={`${summary.totalUnas} UNAs`} />
        <SummaryCard label="UNAs below 30" value={String(summary.below30)} />
        <SummaryCard label="Total overdue items" value={String(summary.overdue)} />
        <SummaryCard label="No record in 90+ days" value={String(summary.stale90)} />
      </div>

      {/* PDF backfill card */}
      <Card>
        <CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-start gap-3">
            <FileWarning className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium">
                Documents missing PDFs:{" "}
                <span className="tabular-nums">
                  {missingPdfs.isLoading ? "…" : missingPdfs.data?.count ?? 0}
                </span>
              </p>
              <p className="text-xs text-muted-foreground">
                Finalized documents with content but no rendered PDF. Idempotent — already-rendered rows are skipped.
              </p>
              {backfillRunning && (
                <p className="text-xs mt-1">
                  Preparing {backfillProgress.done} of {backfillProgress.total}…
                </p>
              )}
              {!backfillRunning && backfillFailures.length > 0 && (
                <div className="mt-2 text-xs">
                  <p className="text-destructive font-medium">
                    {backfillFailures.length} failed:
                  </p>
                  <ul className="list-disc pl-5 max-h-32 overflow-y-auto">
                    {backfillFailures.map((f, i) => (
                      <li key={i}>
                        <span className="font-medium">{f.name}</span> — {f.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          <Button
            size="sm"
            onClick={runBackfill}
            disabled={
              backfillRunning ||
              missingPdfs.isLoading ||
              (missingPdfs.data?.count ?? 0) === 0
            }
          >
            {backfillRunning ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Regenerating…
              </>
            ) : (
              "Regenerate all"
            )}
          </Button>
        </CardContent>
      </Card>


      <Card>
        <CardHeader>
          <CardTitle className="text-base">All UNAs</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left">
                <tr>
                  <Th>UNA</Th>
                  <Th>Secretary Treasurer</Th>
                  <ThSort active={sortKey === "score"} dir={sortDir} onClick={() => toggleSort("score")}>Score</ThSort>
                  <ThSort active={sortKey === "overdue"} dir={sortDir} onClick={() => toggleSort("overdue")}>Overdue</ThSort>
                  <Th>Foundation</Th>
                  <ThSort active={sortKey === "days"} dir={sortDir} onClick={() => toggleSort("days")}>Last record</ThSort>
                  <Th>Journey</Th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Loading…</td></tr>
                ) : sorted.length === 0 ? (
                  <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No UNAs</td></tr>
                ) : sorted.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => setSelectedUna(r)}
                    className="border-t cursor-pointer hover:bg-muted/40"
                  >
                    <td className="p-3 font-medium">{r.name}</td>
                    <td className="p-3">{r.ts_name ?? <span className="text-muted-foreground">—</span>}</td>
                    <td className="p-3">
                      <span className={cn("font-semibold", scoreColor(r.score))}>
                        {r.score ?? "—"}
                      </span>
                    </td>
                    <td className="p-3">{r.overdue_count}</td>
                    <td className="p-3">
                      {r.foundation_satisfied} of {r.foundation_total || 6}
                    </td>
                    <td className="p-3">
                      {r.days_since === null ? (
                        <span className="text-muted-foreground">never</span>
                      ) : (
                        `${r.days_since}d`
                      )}
                    </td>
                    <td className="p-3">
                      <JourneyBadge status={r.journey_status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <UnaDetailDrawer
        una={selectedUna}
        catalog={catalog}
        onClose={() => setSelectedUna(null)}
      />
    </div>
  );
}

function SummaryCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-2xl font-semibold mt-1">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="p-3 font-medium text-muted-foreground">{children}</th>;
}
function ThSort({
  children, active, dir, onClick,
}: { children: React.ReactNode; active: boolean; dir: "asc" | "desc"; onClick: () => void }) {
  return (
    <th className="p-3 font-medium text-muted-foreground">
      <button onClick={onClick} className="inline-flex items-center gap-1 hover:text-foreground">
        {children}
        <ArrowUpDown className={cn("h-3 w-3", active ? "opacity-100" : "opacity-40")} />
        {active && <span className="text-[10px]">{dir}</span>}
      </button>
    </th>
  );
}

function scoreColor(score: number | null) {
  if (score === null) return "text-muted-foreground";
  if (score >= 60) return "text-emerald-700";
  if (score >= 30) return "text-amber-700";
  return "text-red-700";
}

function JourneyBadge({ status }: { status: UnaRow["journey_status"] }) {
  const map = {
    completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    in_progress: { label: "In progress", cls: "bg-amber-50 text-amber-800 border-amber-200" },
    not_started: { label: "Not started", cls: "bg-muted text-muted-foreground border-border" },
  } as const;
  const v = map[status];
  return <Badge variant="outline" className={v.cls}>{v.label}</Badge>;
}

function UnaDetailDrawer({
  una, catalog, onClose,
}: { una: UnaRow | null; catalog: RuleRow[]; onClose: () => void }) {
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["admin-gh-detail", una?.id],
    enabled: !!una?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("una_compliance_items")
        .select("id, rule_key, rule_group, status, due_date, na_reason, period_year")
        .eq("una_id", una!.id);
      if (error) throw error;
      return data ?? [];
    },
  });

  const catalogBy = useMemo(() => {
    const m = new Map<string, RuleRow>();
    catalog.forEach((c) => m.set(c.rule_key, c));
    return m;
  }, [catalog]);

  const grouped = useMemo(() => {
    const g: Record<string, any[]> = {};
    items.forEach((it: any) => {
      const grp = it.rule_group || "foundation";
      (g[grp] ??= []).push(it);
    });
    return g;
  }, [items]);

  return (
    <Sheet open={!!una} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{una?.name}</SheetTitle>
          <SheetDescription>
            {una?.ts_name ? `Secretary: ${una.ts_name} · ` : ""}
            Score: {una?.score ?? "—"} · Overdue: {una?.overdue_count ?? 0}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-6">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            RULE_GROUP_ORDER.map((grp) => {
              const list = grouped[grp] ?? [];
              if (list.length === 0) return null;
              return (
                <div key={grp}>
                  <h3 className="font-semibold text-sm mb-2">
                    {RULE_GROUP_LABELS[grp]}
                  </h3>
                  <ul className="space-y-2">
                    {list.map((it: any) => {
                      const rule = catalogBy.get(it.rule_key);
                      const status = it.status as ItemStatus;
                      return (
                        <li
                          key={it.id}
                          className="border rounded-md p-3 space-y-1"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <p className="text-sm font-medium">
                                {rule?.title ?? it.rule_key}
                              </p>
                              {it.due_date && (
                                <p className="text-xs text-muted-foreground">
                                  Due {it.due_date}
                                </p>
                              )}
                              {it.na_reason && (
                                <p className="text-xs text-muted-foreground italic mt-1">
                                  N/A: {it.na_reason}
                                </p>
                              )}
                            </div>
                            <Badge
                              variant="outline"
                              className={cn("text-[10px]", STATUS_CLASS[status])}
                            >
                              {STATUS_LABEL[status]}
                            </Badge>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })
          )}
        </div>
        <div className="mt-6">
          <Button variant="outline" onClick={onClose} className="w-full">Close</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
